import { useState } from "react";
import { MapPin, Phone, Mail, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    property_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, property_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        property_type: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again or email us directly at info@alturasource.com");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      className="section-padding bg-white"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-cyan-600 font-semibold text-sm tracking-widest uppercase mb-4 block">
            Get In Touch
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6">
            Let's Start Your{" "}
            <span className="text-cyan-600">Project</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Ready to transform your multifamily properties? Get in touch with our 
            team for a personalized quote.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2">
            <div className="bg-slate-900 rounded-lg p-8 h-full">
              <h3 className="text-2xl font-bold text-white mb-6">
                Contact Information
              </h3>
              <p className="text-slate-400 mb-8">
                Fill out the form and our team will get back to you within 24 hours.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-cyan-600/20 rounded-md flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Phone</p>
                    <a 
                      href="tel:6785499515" 
                      className="text-slate-400 hover:text-cyan-400 transition-colors"
                      data-testid="contact-phone"
                    >
                      678.549.9515
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-cyan-600/20 rounded-md flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Email</p>
                    <a 
                      href="mailto:info@alturasource.com" 
                      className="text-slate-400 hover:text-cyan-400 transition-colors"
                      data-testid="contact-email"
                    >
                      info@alturasource.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-cyan-600/20 rounded-md flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Service Areas</p>
                    <p className="text-slate-400">
                      Nationwide
                    </p>
                  </div>
                </div>
              </div>

              {/* Decorative */}
              <div className="mt-12 pt-8 border-t border-slate-700">
                <p className="text-slate-400 text-sm">
                  Available Monday - Friday, 8:00 AM - 6:00 PM EST
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            {isSubmitted ? (
              <div className="bg-slate-50 rounded-lg p-12 text-center border border-slate-200">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-cyan-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Thank You!
                </h3>
                <p className="text-slate-600 mb-8">
                  Your message has been received. Our team will contact you shortly.
                </p>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                  className="border-cyan-600 text-cyan-600 hover:bg-cyan-50"
                  data-testid="send-another-btn"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form 
                onSubmit={handleSubmit} 
                className="bg-slate-50 rounded-lg p-8 border border-slate-200"
                data-testid="contact-form"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700 font-medium">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      required
                      className="form-input border-slate-300 focus:border-cyan-500 focus:ring-cyan-500"
                      data-testid="contact-name-input"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      required
                      className="form-input border-slate-300 focus:border-cyan-500 focus:ring-cyan-500"
                      data-testid="contact-email-input"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 font-medium">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="form-input border-slate-300 focus:border-cyan-500 focus:ring-cyan-500"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  {/* Company */}
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-slate-700 font-medium">
                      Company Name
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="form-input border-slate-300 focus:border-cyan-500 focus:ring-cyan-500"
                      data-testid="contact-company-input"
                    />
                  </div>

                  {/* Property Type */}
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="property_type" className="text-slate-700 font-medium">
                      Property Type
                    </Label>
                    <Select 
                      value={formData.property_type} 
                      onValueChange={handleSelectChange}
                    >
                      <SelectTrigger 
                        className="border-slate-300 focus:border-cyan-500 focus:ring-cyan-500"
                        data-testid="contact-property-select"
                      >
                        <SelectValue placeholder="Select property type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-100 Units">1-100 Units</SelectItem>
                        <SelectItem value="100-250 Units">100-250 Units</SelectItem>
                        <SelectItem value="250+ Units">250+ Units</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Message */}
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="message" className="text-slate-700 font-medium">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project requirements, number of units, timeline, etc."
                      required
                      rows={5}
                      className="form-input border-slate-300 focus:border-cyan-500 focus:ring-cyan-500 resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-8 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-6 text-lg rounded-md"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
